export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Formats an array of month numbers (1-12) into a human-readable range string.
 * Handles wrap-around (e.g., Dec-Jan displays as "Dec-Jan", not "Jan, Dec").
 *
 * @example
 * formatMonthRange([1, 2, 3]) // "Jan-Mar"
 * formatMonthRange([11, 12, 1, 2]) // "Nov-Feb"
 * formatMonthRange([12, 1, 3, 4]) // "Dec-Jan, Mar-Apr"
 * formatMonthRange([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]) // "Year-round"
 */
export function formatMonthRange(months: number[]): string {
  if (months.length === 12) return "Year-round";
  if (months.length === 0) return "";
  if (months.length === 1) return MONTH_NAMES[months[0] - 1] ?? "";

  const monthSet = new Set(months);
  const getName = (m: number) => MONTH_NAMES[m - 1] ?? "";

  // Find all gap positions (where a selected month is followed by a non-selected month)
  const gaps: number[] = [];
  for (let m = 1; m <= 12; m++) {
    const next = m === 12 ? 1 : m + 1;
    if (monthSet.has(m) && !monthSet.has(next)) {
      gaps.push(m);
    }
  }

  // Build ranges using gap positions (handles wrap-around naturally)
  const ranges: string[] = [];
  for (let i = 0; i < gaps.length; i++) {
    const rangeEnd = gaps[i];
    const prevGap = gaps[(i - 1 + gaps.length) % gaps.length];
    let rangeStart = prevGap === 12 ? 1 : prevGap + 1;
    while (!monthSet.has(rangeStart)) {
      rangeStart = rangeStart === 12 ? 1 : rangeStart + 1;
    }

    if (rangeStart === rangeEnd) {
      ranges.push(getName(rangeStart));
    } else {
      ranges.push(`${getName(rangeStart)}-${getName(rangeEnd)}`);
    }
  }

  return ranges.join(", ");
}
